import {
	Component, Output, OnInit, OnChanges, SimpleChanges, OnDestroy,
	EventEmitter
} from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { Observable, Subject } from 'rxjs/Rx';
import { MessageService } from 'base';
import { MonitorService } from 'monitor';
import { IpAddress, Device } from 'DiscoveryEngine.TS/Core';
import {
	MonitorPolicy, MonitorSettingsEntry, MonitorSettings_Ping, MonitorSettings_IpEntry
} from 'DiscoveryEngine.TS/Monitors';
import { List, ValueEncoder, Guid } from 'DiscoveryEngine.TS/Utils';
import { MonitorConfigComponentBase } from '../MonitorConfigComponentBase';
import { IdentityService } from 'identity';
import { NavigationService } from 'netview';
import { DiscResultsService } from 'discovery';
import {
	GetDeviceResultDto, GetMonitorStateResultDto, MonitorStateDto, DisplayDeviceListEntryDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { MonitorDeviceSelectorResult } from '../monitor-device-selector/monitor-device-selector.component';
import { MonitorEntry } from 'DiscoveryEngine.TS/InfoSets/MonitorTable';


export class PingMonitorListEntry {
	public deviceGuid: string;
	public settingsEntryGuid: string;
	public deviceName: string;
	public deviceDescription: string;
	public ipAddress: string;
	public hostname: string;
	public state: string;
	public stateDetails: string;
	public style: string;

	public deviceTag: Device;
	public get hasDeviceTag(): boolean {
		return !!this.deviceTag;
	}

	public ipAddressTag: IpAddress;
	public get hasIpAddressTag(): boolean {
		return !!this.ipAddressTag;
	}

	public settingsEntryTag: MonitorSettings_IpEntry;
	public get hasSettingsEntryTag(): boolean {
		return !!this.settingsEntryTag;
	}

	constructor(deviceGuid?: string, device?: Device, settingsEntryGuid?: string, settingsEntry?: MonitorSettings_IpEntry, settingsEntryState?: string,
		settingsEntryStateDetails?: string) {
		if (!!deviceGuid) {
			this.deviceGuid = deviceGuid;
			this.settingsEntryGuid = settingsEntryGuid;

			if (!!device) {
				this.deviceName = device.DisplayName;
				this.deviceDescription = device.DisplayDescription;
			}
			else {
				this.deviceName = deviceGuid;
				this.deviceDescription = "";
			}

			if (settingsEntry.HasIpAddress) {
				this.ipAddress = settingsEntry.IpAddress.ToString();
				this.ipAddressTag = settingsEntry.IpAddress;
			}
			else {
				this.ipAddress = "";
				this.ipAddressTag = null;
			}

			if (settingsEntry.HasHostname) {
				this.hostname = settingsEntry.Hostname;
			}
			else {
				this.hostname = "";
			}

			if (!!settingsEntryState) {
				this.state = settingsEntryState;
				if (this.state === "NoData") {
					this.state = "No Data";
				}

				this.style = "monitor-state-" + settingsEntryState.toLowerCase();
			}
			else {
				this.state = "No Data";
				this.style = "monitor-state-nodata";
			}

			if (!!settingsEntryStateDetails) {
				this.stateDetails = settingsEntryStateDetails;
			}
			else {
				this.stateDetails = "";
			}

			this.deviceTag = device;
			this.settingsEntryTag = settingsEntry;
		}
		else {
			this.deviceGuid = "";
			this.settingsEntryGuid = "";
			this.deviceName = "";
			this.deviceDescription = "";
			this.ipAddress = "";
			this.hostname = "";
			this.state = "No Data";
			this.style = "monitor-state-nodata";
			this.stateDetails = "";

			this.deviceTag = null;
			this.ipAddressTag = null;
			this.settingsEntryTag = null;
		}
	}
}

@Component({
	selector: 'ping-devices',
	templateUrl: './pingdevices.component.html',
	styleUrls: ['./pingdevices.component.scss']
})
export class PingDevicesComponent extends MonitorConfigComponentBase implements OnInit, OnChanges, OnDestroy {

	@Output()
	public applyTempChangesIfValid: EventEmitter<any>;

	public entries: PingMonitorListEntry[];
	public deviceSelectorVisible: boolean;
	public entryEditorVisible: boolean;
	public entryEditorDevice: Device;
	public entryEditorTarget: MonitorSettings_IpEntry;
	public entryEditorCreate: boolean;
	public entryEditorIpAddresses: IpAddress[];
	public entryEditorShowIpV6: boolean;

	private deviceCache: Object;
	private stateCache: Object;

	constructor(private monService: MonitorService,
		private messageService: MessageService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private discResultsService: DiscResultsService,
		private fb: UntypedFormBuilder) {

		super();

		this.applyTempChangesIfValid = new EventEmitter<any>();

		this.entries = [];
		this.deviceSelectorVisible = false;
		this.initEntryEditorParams();

		this.deviceCache = {};
		this.stateCache = {};
	}

	private initEntryEditorParams(): void {
		this.entryEditorVisible = false;
		this.entryEditorDevice = null;
		this.entryEditorTarget = null;
		this.entryEditorCreate = false;
		this.entryEditorIpAddresses = [];
		this.entryEditorShowIpV6 = false;
	}

	public ngOnInit(): void {
		return;
	}

	public ngOnChanges(changes: SimpleChanges): void {

		if (this.visible) {
			this.entries = [];
			this.retrieveDevicesAndMonitorStates();
		}
	}

	public ngOnDestroy(): void {
		return;
	}

	private retrieveDevicesAndMonitorStates(): void {

		this.deviceCache = {};
		this.stateCache = {};

		if (!!this.editor) {
			if (this.editor.Policy.DeviceGuids.Count > 0) {
				let deviceGuidStrs = new List<string>();
				this.editor.Policy.DeviceGuids.ForEach((guid: Guid) => {
					let guidStr = ValueEncoder.EncodeGuid(guid);
					deviceGuidStrs.Add(guidStr);
				});

				this.getDevices(deviceGuidStrs).subscribe(
					(gdResult: any) => {
						this.getMonitorState(this.editor.Policy).subscribe(
							(gmsResult: any) => {
								this.rebuildEntries();
							},
							(gmsError) => {
								this.rebuildEntries();
								this.messageService.displayErrorMessage("Could not retrieve monitor state info");
							});
					},
					(gdError) => {
						this.messageService.displayErrorMessage("Could not retrieve monitor device info");
					});
			}
			else {
				this.rebuildEntries();
			}
		}
	}

	private rebuildEntries(): void {

		this.entries = [];

		let monPolicy: MonitorPolicy = this.editor.Policy;
		monPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {

			let monEntry: MonitorEntry = monPolicy.DeviceMonitorMap.GetMonitor_DeviceGuid(deviceGuid);
			monEntry.Settings.GetEntries().ForEach((monSetsEntry: MonitorSettingsEntry) => {

				let deviceGuidStr = ValueEncoder.EncodeGuid(deviceGuid);
				let monSetsEntryGuidStr = ValueEncoder.EncodeGuid(monSetsEntry.Guid);

				let device: Device = null;
				let state: string = "";
				let stateDetails: string = "";

				if (!!this.deviceCache[deviceGuidStr]) {
					device = this.deviceCache[deviceGuidStr];
				}

				if (!!this.stateCache[monSetsEntryGuidStr]) {
					let monState: MonitorStateDto = this.stateCache[monSetsEntryGuidStr];
					state = monState.monitorSettingsEntryState;
					stateDetails = monState.monitorSettingsEntryStateDetails;
				}

				let monSetsIpEntry = <MonitorSettings_IpEntry>(monSetsEntry);

				let entry = new PingMonitorListEntry(deviceGuidStr, device, monSetsEntryGuidStr, monSetsIpEntry,
					state, stateDetails);
				this.entries.push(entry);
			});
		});
	}

	private getDevices(deviceGuids: List<string>): Observable<any> {

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, "monitor-network", deviceGuids, false).subscribe(
				(result: GetDeviceResultDto) => {
					result.Devices.ForEach((value: Device) => {
						let deviceGuidStr = ValueEncoder.EncodeGuid(value.GUID);
						this.deviceCache[deviceGuidStr] = value;
					});

					subject.next(true);
				},
				(error) => {
					subject.error(false);
				});

		return subject.asObservable();
	}


	private getMonitorState(policy: MonitorPolicy): Observable<any> {

		this.applyTempChangesIfValid.emit();

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.monService.getMonitorState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, policy, false).subscribe(
				(result: GetMonitorStateResultDto) => {
					result.entries.forEach((entry: MonitorStateDto) => {
						this.stateCache[entry.monitorSettingsEntryGuid] = entry;
					});

					subject.next(true);
				},
				(error) => {
					subject.error(false);
				});

		return subject.asObservable();
	}

	public BuildForm(): void {
		this.SettingsForm = this.fb.group({
		});
	}

	public applyTempChanges(): void {
		if (!!this.editor && this.editor.HasPolicy) {

			let monPolicy: MonitorPolicy = this.editor.Policy;
			let monSettings: MonitorSettings_Ping = <MonitorSettings_Ping>monPolicy.Settings;

			this.BuildForm();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["Devices"];
	}

	public addDevice(): void {
		this.deviceSelectorVisible = true;
	}

	public enableAddIpMonitor(entry: PingMonitorListEntry): boolean {
		return (this.getCreateEntryIpAddresses(entry).length > 0);
	}

	public addIpMonitor(entry: PingMonitorListEntry): void {
		this.entryEditorVisible = true;
		this.entryEditorDevice = entry.deviceTag;
		this.entryEditorTarget = new MonitorSettings_IpEntry();
		this.entryEditorCreate = true;
		this.entryEditorIpAddresses = this.getCreateEntryIpAddresses(entry);
		this.entryEditorShowIpV6 = false;
	}

	public editSettings(entry: PingMonitorListEntry): void {
		this.entryEditorVisible = true;
		this.entryEditorDevice = entry.deviceTag;
		this.entryEditorTarget = entry.settingsEntryTag;
		this.entryEditorCreate = false;
		this.entryEditorIpAddresses = this.getEditEntryIpAddresses(entry);
		this.entryEditorShowIpV6 = true;
	}

	private getEditEntryIpAddresses(entry: PingMonitorListEntry): IpAddress[] {
		return [entry.ipAddressTag];
	}

	private getCreateEntryIpAddresses(entry: PingMonitorListEntry): IpAddress[] {

		let result: IpAddress[] = [];

		let deviceEntries: PingMonitorListEntry[] = this.getAllEntriesForDevice(entry.deviceGuid);

		let device: Device = entry.deviceTag;

		device.IpAddresses.ForEach((ip: IpAddress) => {
			if (!deviceEntries.some(entry => entry.ipAddressTag.Equals(ip))) {
				result.push(ip);
			}
		});

		return result;
	}

	private getAllEntriesForDevice(deviceGuid: string): PingMonitorListEntry[] {

		let deviceEntries: PingMonitorListEntry[] = [];

		this.entries.forEach((entry: PingMonitorListEntry) => {
			if (entry.deviceGuid === deviceGuid) {
				deviceEntries.push(entry);
			}
		});

		return deviceEntries;
	}

	public deleteDevice(entry: PingMonitorListEntry): void {

		let deviceEntries: PingMonitorListEntry[] = this.getAllEntriesForDevice(entry.deviceGuid);

		deviceEntries.forEach((devEntry: PingMonitorListEntry) => {
			let b = this.editor.Remove_PingIpEntry(devEntry.deviceTag, devEntry.settingsEntryTag);
			console.assert(b);
		});

		this.rebuildEntries();
	}

	public deleteIpMonitor(entry: PingMonitorListEntry): void {

		let b = this.editor.Remove_PingIpEntry(entry.deviceTag, entry.settingsEntryTag);
		console.assert(b);
		this.rebuildEntries();
	}

	public refreshEntries(): void {
		this.retrieveDevicesAndMonitorStates();
	}

	public onEntryEditorSave(): void {

		let createdEntry: MonitorSettings_IpEntry = null;

		if (this.entryEditorCreate) {
			let devicePingSettings: MonitorSettings_Ping =
				this.editor.GetMonitorSettings_Ping(this.entryEditorDevice);

			createdEntry = devicePingSettings.Add_IpHostPollBy(this.entryEditorTarget.IpAddress,
				this.entryEditorTarget.Hostname, this.entryEditorTarget.PollByHostname);

			// Make sure new monitor entries have GUIDs
			this.editor.Policy.VerifyGuids();
		}
		else {
			// Entry was edited in-place
		}

		this.editor.HasChanges = true;

		this.initEntryEditorParams();

		this.rebuildEntries();
	}

	public onEntryEditorCancel(): void {
		this.initEntryEditorParams();
	}

	public onDeviceSelectorOk(result: MonitorDeviceSelectorResult): void {

		let devicesToAdd = new List<string>();
		result.devices.forEach((value: DisplayDeviceListEntryDto) => {
			let deviceGuid = Guid.FromString(value.deviceGuid);
			if (!this.editor.Policy.DeviceGuids.Contains(deviceGuid)) {
				devicesToAdd.Add(value.deviceGuid);
			}
		});

		if (devicesToAdd.Count > 0) {
			this.getDevices(devicesToAdd).subscribe(
				(gdResult: any) => {
					let addedDeviceCount = 0;
					let deviceGuidToSelect: string = "";

					devicesToAdd.ForEach((deviceGuidStr: string) => {
						let device: Device = this.deviceCache[deviceGuidStr];
						if (!!device) {
							if (this.editor.AddPingMonitor(device)) {
								++addedDeviceCount;
								deviceGuidToSelect = deviceGuidStr;
							}
						}
					});

					if (addedDeviceCount > 0) {
						// Make sure new monitor entries have GUIDs
						this.editor.Policy.VerifyGuids();

						this.getMonitorState(this.editor.Policy).subscribe(
							(gmsResult: any) => {
								this.rebuildEntries();
							},
							(gmsError) => {
								this.rebuildEntries();
								this.messageService.displayErrorMessage("Could not retrieve monitor state info");
							});
					}

					if (addedDeviceCount < devicesToAdd.Count) {
						this.messageService.displayErrorMessage("Error adding device(s)");
					}
				},
				(gdError) => {
					this.messageService.displayErrorMessage("Could not retrieve monitor device info");
				});
		}
		else {
			this.messageService.displayInfoMessage("No new devices were added");
		}
	}

	public onDeviceSelectorCancel(): void {
		return;
	}

	public doCustomSort(event): void {
		let comparer = function (a: PingMonitorListEntry, b: PingMonitorListEntry): number {
			let result = 0;

			switch (event.field) {
				case "ipAddress":
					if (a.ipAddressTag && b.ipAddressTag) {
						result = a.ipAddressTag.CompareTo(b.ipAddressTag);
					}
					else if (a.ipAddressTag) {
						result = 1;
					}
					else if (b.ipAddressTag) {
						result = -1;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].toString().localeCompare(b[event.field].toString());
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

}
